import { Form, Formik } from 'formik';
import { withZodSchema } from 'formik-validator-zod';
import { Box, Button, DateInput, FormField, MaskedInput, Select, Text, TextArea, TextInput } from 'grommet';
import { Certificate, DocumentUpload } from 'grommet-icons';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as z from 'zod';
import { AddNewButton, ModalInfoCard } from '..';
import { Commercial, DocumentType, Right, RightStatus, RightType, Document as TalonDocument } from '../../../../lib/api';
import { useCommercialStore, useRightStore } from '../../../context';
import { fromDateInputChange, toDateOnly } from '../../../utils/dateOnly';
import { ModalCard } from '../ModalCard';
import { DocumentLink } from '../project-home/DocumentLink';
import { DocumentUploadModal } from '../project-home/DocumentUploadModal';

interface RightDetailsModalProps {
  right: Partial<Right> | null;
  onClose: () => void;
  onSuccess: () => void;
  mode?: 'create' | 'edit';
  advertiserId?: string;
  projectId?: string;
}

const RightSchema = z.object({
  description: z.string().optional(),
  rightType: z.nativeEnum(RightType),
  status: z.nativeEnum(RightStatus).optional(),
  termFromDate: z.string().min(1, 'Start date is required'),
  termToDate: z.string().optional(),
  media: z.string().min(1, 'Media is required'),
  territories: z.string().min(1, 'Territories is required'),
  licensor: z.string().min(1, 'Licensor is required'),
  licensee: z.string().min(1, 'Licensee is required'),
  rightsAdmin: z.string().min(1, 'Rights admin is required'),
  phone: z.string().min(1, 'Phone is required'),
  email: z.string().email('Invalid email format').min(1, 'Email is required'),
  restrictionsOnUse: z.string().optional(),
  notes: z.string().optional(),
  document: z.custom<TalonDocument>().optional().nullable(),
  commercialIds: z.array(z.string()).optional(),
});

export type RightFormValues = z.infer<typeof RightSchema>;

export const RightDetailsModal: React.FC<RightDetailsModalProps> = ({
  right,
  onClose,
  onSuccess,
  mode = 'edit',
  advertiserId,
  projectId,
}) => {
  const rightStore = useRightStore();
  const commercialStore = useCommercialStore();
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [commercials, setCommercials] = useState<Commercial[]>([]);

  useEffect(() => {
    const fetchCommercials = async () => {
      if (!projectId) return;
      const fetched = await commercialStore.listCommercialsByProjectId(projectId);
      if (fetched) setCommercials(fetched);
    };

    fetchCommercials();
  }, [projectId, commercialStore]);

  const handleSubmit = async (values: RightFormValues) => {
    if (mode === 'edit' && !right) {
      toast.error('Right not found');
      return;
    }
    if (loading) return;

    try {
      setLoading(true);

      if (mode === 'create') {
        // Create new right
        const createRequest = {
          description: values.description,
          rightType: values.rightType,
          status: values.status,
          termFromDate: values.termFromDate,
          termToDate: values.termToDate,
          media: values.media,
          territories: values.territories,
          licensor: values.licensor,
          licensee: values.licensee,
          rightsAdmin: values.rightsAdmin,
          phone: values.phone,
          email: values.email,
          restrictionsOnUse: values.restrictionsOnUse,
          notes: values.notes,
          advertiserId: advertiserId,
          documentId: values.document?.id || undefined,
          commercialIds: values.commercialIds,
        };

        const result = await rightStore.createRight(createRequest);

        if (result) {
          toast.success('Right created successfully');
          onSuccess();
        } else {
          toast.error('Failed to create right');
        }
      } else {
        // Update existing right
        if (!right || !right.id) {
          toast.error('Right data is missing');
          return;
        }

        const updatedRight: Partial<Right> & Pick<Right, 'id'> = {
          ...right,
          id: right.id,
          description: values.description || '',
          rightType: values.rightType,
          status: values.status,
          termFromDate: values.termFromDate,
          termToDate: values.termToDate || undefined,
          media: values.media,
          territories: values.territories,
          licensor: values.licensor,
          licensee: values.licensee,
          rightsAdmin: values.rightsAdmin,
          phone: values.phone,
          email: values.email,
          restrictionsOnUse: values.restrictionsOnUse || '',
          notes: values.notes || '',
          commercials: commercials.filter((c) => (values.commercialIds || []).includes(c.id)),
        };

        const result = await rightStore.updateRight(updatedRight, values.document?.id || undefined);

        if (result) {
          toast.success('Right updated successfully');
          onSuccess();
        } else {
          toast.error('Failed to update right');
        }
      }
    } catch (error) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} right:`, error);
      toast.error(`Failed to ${mode === 'create' ? 'create' : 'update'} right`);
    } finally {
      setLoading(false);
    }
  };

  const initialValues: RightFormValues = {
    description: right?.description || '',
    rightType: right?.rightType || RightType.OTHER,
    status: right?.status || RightStatus.ACTIVE,
    termFromDate: toDateOnly(right?.termFromDate),
    termToDate: toDateOnly(right?.termToDate),
    media: right?.media || '',
    territories: right?.territories || '',
    licensor: right?.licensor || '',
    licensee: right?.licensee || '',
    rightsAdmin: right?.rightsAdmin || '',
    phone: right?.phone || '',
    email: right?.email || '',
    restrictionsOnUse: right?.restrictionsOnUse || '',
    notes: right?.notes || '',
    document: right?.document || null,
    commercialIds: right?.commercials?.map((c) => c.id) || [],
  };

  const rightTypeOptions = Object.values(RightType).map((type) => ({ label: type, value: type }));
  const statusOptions = Object.values(RightStatus).map((status) => ({ label: status, value: status }));
  const commercialOptions = commercials.map((c) => ({ label: c.title, value: c.id }));

  const documentUploadCallback = (values: RightFormValues) => {
    if (!values.document) return;
    if (mode === 'edit') {
      // save the document to the right immediately
      handleSubmit({ ...values });
    }
  };

  return (
    <ModalCard
      onClose={onClose}
      title={mode === 'create' ? 'Create New Right' : 'Edit Right Details'}
      icon={<Certificate size="24px" />}
    >
      <Formik
        initialValues={initialValues}
        validate={withZodSchema(RightSchema)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, dirty, setFieldValue }) => (
          <Form>
            <Box gap="medium">
              {/* Basic Information */}
              <ModalInfoCard title="Basic Information">
                <FormField label="Description" name="description" error={touched.description && errors.description}>
                  <TextInput
                    id="description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Brief description of the right"
                  />
                </FormField>

                <Box gap="medium" direction="row">
                  <FormField
                    label="Right Type"
                    name="rightType"
                    error={touched.rightType && errors.rightType}
                    required
                    width="50%"
                  >
                    <Select
                      id="rightType"
                      name="rightType"
                      value={values.rightType}
                      options={rightTypeOptions}
                      onChange={({ option }) => setFieldValue('rightType', option.value)}
                      onBlur={handleBlur}
                    />
                  </FormField>

                  <FormField label="Status" name="status" error={touched.status && errors.status} width="50%">
                    <Select
                      id="status"
                      name="status"
                      value={values.status}
                      options={statusOptions}
                      onChange={({ option }) => setFieldValue('status', option.value)}
                      onBlur={handleBlur}
                    />
                  </FormField>
                </Box>

                <FormField
                  label="Commercials"
                  name="commercialIds"
                  error={touched.commercialIds && errors.commercialIds}
                  help="Leave empty to apply this right to all commercials in this production"
                >
                  <Select
                    id="commercialIds"
                    name="commercialIds"
                    multiple
                    closeOnChange={false}
                    placeholder="All Commercials"
                    value={values.commercialIds}
                    options={commercialOptions}
                    valueKey={{ key: 'value', reduce: true }}
                    labelKey="label"
                    onChange={({ value }) => setFieldValue('commercialIds', value)}
                    disabled={commercialOptions.length === 0}
                  />
                </FormField>
              </ModalInfoCard>

              {/* Term Information */}
              <ModalInfoCard title="Term Information">
                <Box gap="medium" direction="row">
                  <FormField
                    label="Term From Date"
                    name="termFromDate"
                    error={touched.termFromDate && errors.termFromDate}
                    required
                    width="50%"
                  >
                    <DateInput
                      id="termFromDate"
                      name="termFromDate"
                      value={values.termFromDate}
                      onChange={({ value }) => setFieldValue('termFromDate', fromDateInputChange(value))}
                      onBlur={handleBlur}
                      // "yyyy-mm-dd" (not "mm/dd/yyyy" like the rest of the app) was
                      // misdetected as a range format by Grommet, since its internal
                      // `range` check is just `format.includes('-')` - it doesn't
                      // distinguish a single date's own separator from an actual
                      // "start - end" range format. That silently turned every typed
                      // value into a one-element array and corrupted the sibling field.
                      format="mm/dd/yyyy"
                    />
                  </FormField>

                  <FormField
                    label="Term To Date"
                    name="termToDate"
                    error={touched.termToDate && errors.termToDate}
                    width="50%"
                  >
                    <DateInput
                      id="termToDate"
                      name="termToDate"
                      value={values.termToDate}
                      onChange={({ value }) => setFieldValue('termToDate', fromDateInputChange(value))}
                      onBlur={handleBlur}
                      format="mm/dd/yyyy"
                    />
                  </FormField>
                </Box>

                <FormField label="Media" name="media" error={touched.media && errors.media} required>
                  <TextInput
                    id="media"
                    name="media"
                    value={values.media}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="TV, Radio, Digital, etc."
                  />
                </FormField>

                <FormField
                  label="Territories"
                  name="territories"
                  error={touched.territories && errors.territories}
                  required
                >
                  <TextInput
                    id="territories"
                    name="territories"
                    value={values.territories}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="United States, Canada, etc."
                  />
                </FormField>
              </ModalInfoCard>

              {/* Contact Information */}
              <ModalInfoCard title="Contact Information">
                <Box gap="medium" direction="row">
                  <FormField
                    label="Licensor"
                    name="licensor"
                    error={touched.licensor && errors.licensor}
                    required
                    width="50%"
                  >
                    <TextInput
                      id="licensor"
                      name="licensor"
                      value={values.licensor}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Licensing company or individual"
                    />
                  </FormField>

                  <FormField
                    label="Licensee"
                    name="licensee"
                    error={touched.licensee && errors.licensee}
                    required
                    width="50%"
                  >
                    <TextInput
                      id="licensee"
                      name="licensee"
                      value={values.licensee}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Licensed company or individual"
                    />
                  </FormField>
                </Box>

                <FormField
                  label="Rights Administrator"
                  name="rightsAdmin"
                  error={touched.rightsAdmin && errors.rightsAdmin}
                  required
                >
                  <TextInput
                    id="rightsAdmin"
                    name="rightsAdmin"
                    value={values.rightsAdmin}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Person managing this right"
                  />
                </FormField>

                <Box gap="medium" direction="row">
                  <FormField label="Phone" name="phone" error={touched.phone && errors.phone} width="50%">
                    <MaskedInput
                      mask={[
                        { fixed: '(' },
                        {
                          length: 3,
                          regexp: /^[0-9]{1,3}$/,
                          placeholder: 'xxx',
                        },
                        { fixed: ')' },
                        { fixed: ' ' },
                        {
                          length: 3,
                          regexp: /^[0-9]{1,3}$/,
                          placeholder: 'xxx',
                        },
                        { fixed: '-' },
                        {
                          length: 4,
                          regexp: /^[0-9]{1,4}$/,
                          placeholder: 'xxxx',
                        },
                      ]}
                      id="phone"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormField>

                  <FormField label="Email" name="email" error={touched.email && errors.email} required width="50%">
                    <TextInput
                      id="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      type="email"
                      placeholder="Contact email address"
                    />
                  </FormField>
                </Box>
              </ModalInfoCard>

              {/* Additional Information */}
              <ModalInfoCard title="Additional Information">
                <FormField
                  label="Restrictions on Use"
                  name="restrictionsOnUse"
                  error={touched.restrictionsOnUse && errors.restrictionsOnUse}
                >
                  <TextArea
                    id="restrictionsOnUse"
                    name="restrictionsOnUse"
                    value={values.restrictionsOnUse}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Any restrictions or limitations on usage"
                    rows={3}
                  />
                </FormField>

                <FormField label="Notes" name="notes" error={touched.notes && errors.notes}>
                  <TextArea
                    id="notes"
                    name="notes"
                    value={values.notes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Additional notes or comments"
                    rows={3}
                  />
                </FormField>
              </ModalInfoCard>

              {/* Footer */}
              <Box direction="row" gap="medium" justify="between" align="center">
                {values?.document ? (
                  <Box direction="column" gap="small" align="center">
                    <DocumentLink doc={values.document} />
                    <Text size="small">{values.document.name}</Text>
                  </Box>
                ) : (
                  <AddNewButton
                    onClick={() => setShowUploadModal(true)}
                    icon={<DocumentUpload />}
                    tipText="Upload License/Rights Document"
                  />
                )}

                <Box direction="row" gap="medium" justify="end" margin={{ vertical: 'small' }}>
                  <Button label="Cancel" onClick={onClose} disabled={loading || isSubmitting} />
                  <Button
                    primary
                    type="submit"
                    label={mode === 'create' ? 'Create Right' : 'Save Changes'}
                    disabled={loading || isSubmitting || (!dirty && mode === 'edit')}
                  />
                </Box>
              </Box>
            </Box>
            {showUploadModal && (
              <DocumentUploadModal
                setShowUpload={setShowUploadModal}
                allowAi={mode === 'create'}
                documentTypes={[DocumentType.License]}
                successCallback={(doc) => {
                  setFieldValue('document', doc);
                  documentUploadCallback(values);
                }}
              />
            )}
          </Form>
        )}
      </Formik>
    </ModalCard>
  );
};

export default RightDetailsModal;
